/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';

import { UtMatrixStandardEquipment } from 'models/ut/UtMatrixStandardEquipment';
import { DataType, addClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { useMessageBar } from '../../../hooks/useMessageBar';
import { ManagementMode } from '../../../models/common/Common';
import { Button } from '@mui/material';
import { IconButton } from '../../../components/buttons/IconSVG';
import { CrudCode } from '../../../models/common/Edit';
import StandardEquipmentPopup from '../popup/StandardEquipmentPopup';
import {
  checkAvailableRequest,
  findMultiUtMatrixAllLibraryList,
  findMultiUtMatrixLibraryList,
  findUtMatrixAllLibraryList,
} from '../../../apis/ut/UtMatrixLibrary';
import { GridStatusCellTemplate } from '../../../components/grids/GridStatusCellRenderer';
import { Code } from '../../../models/common/CommonCode';
import { getCommonCodeNames } from '../../../apis/admin/CommonCode';
import { UtMatrixLibrary } from '../../../models/ut/UtMatrixLibrary';
import useEvent from 'react-use-event-hook';
import { convertNumber } from 'utils/UtMatrixUtil';

interface Props {
  mode: ManagementMode;
  masterTaget?: UtMatrixLibrary[];
  aprReqId: string;
  isReadOnly?: boolean;
  onRefresh: () => void;
  onChange: (masterList: UtMatrixLibrary[]) => void;
  onClickDetail: (target: UtMatrixLibrary) => void;
  gridHeight: number;
}

interface TargetCondition {
  row: number;
  col: number;
}

const EditUtMatrixLibraryGrid = (
  {
    mode,
    masterTaget,
    aprReqId,
    onRefresh,
    onChange,
    onClickDetail,
    isReadOnly = false,
    gridHeight = 570,
  }: Props,
  ref
) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [loaded, setLoaded] = useState(false);

  //const [rowData, setRowData] = useState<UtMatrixLibrary[]>(props?.rowData || []);
  //const [masterData, setMasterData] = useState<UtMatrixStandardEquipment>(props?.mData);

  const [masterTarget, setMasterTarget] = useState<UtMatrixStandardEquipment[]>(masterTaget || []);
  const [rowData, setRowData] = useState<UtMatrixLibrary[]>([]);
  const [selectedRow, setSelectedRow] = useState<UtMatrixLibrary[]>([]);

  const [isOpenStandardEquipmentMultiModal, setOpenStandardEquipmentMultiModal] =
    useState<boolean>(false);
  const [isOpenStandardEquipmentModal, setOpenStandardEquipmentModal] = useState<boolean>(false);
  const [isOpenEquipmentGroupPopup, setOpenEquipmentGroupPopup] = useState<boolean>(false);
  const [targetCondition, setTargetCondition] = useState<TargetCondition>();

  const [code, setCode] = useState<any>();
  const isCreate = useMemo(() => ManagementMode.CREATE === mode && !isReadOnly, [mode, isReadOnly]);

  const [subTitle, setSubTitle] = useState<string>('');

  const isEditable = useMemo(
    () => [ManagementMode.CREATE, ManagementMode.MODIFY, ManagementMode.DELETE].includes(mode),
    [mode]
  );

  // const [masterData, setMasterData] = useState<UtMatrixStandardEquipment[]>(props?.rowData || []);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      if (gridRef.current) {
        gridRef.current?.collectionView.refresh();
      }
    },
  }));

  const getCommonCodesForGrid = async () => {
    const utVltgList: Code[] = await getCommonCodeNames('UT_VLTG_LIST');
    const utVltgPhasMapList: Code[] = await getCommonCodeNames('UT_VLTG_PHAS_MAP_LIST');
    const elpwPhasCd: Code[] = await getCommonCodeNames('ELPW_PHAS_CD');
    const ctwTpCd: Code[] = await getCommonCodeNames('CTW_TP_CD');

    setCode({
      utVltgList: utVltgList,
      elpwPhasCd: elpwPhasCd,
      ctwTpCd: ctwTpCd,
      utVltgPhasMapList: utVltgPhasMapList,
    });
  };

  useEffect(() => {
    getCommonCodesForGrid();
  }, []);

  useEffect(() => {
    //setSubTitle(masterData?.stndEqpId || '');
    if (!aprReqId) {
      setRowData([]);
      return;
    }

    const setDetailData = async () => {
      // 상세 목록이 없는 경우 조회
      if (!(masterTaget || []).length && mode && mode !== ManagementMode.CREATE) {
        const ids = (masterTaget || []).map((item) => item.stndEqpId);

        handleSearch({
          aprReqId: aprReqId,
          stndEqpIds: ids,
          curVerYn: 'Y',
        });
      } else {
        if ((masterTaget || []).length && mode && mode !== ManagementMode.CREATE) {
          if (!loaded) {
            const updatedRow = (masterTaget || []).map(
              (item) =>
                ({
                  ...item,
                  utmLibVerNo:
                    (typeof item.utmLibVerNo === 'number'
                      ? item.utmLibVerNo
                      : Number(item.utmLibVerNo ?? 0)) + 1,
                } as UtMatrixLibrary)
            );
            setRowData(updatedRow);
            //onChange(updatedRow);
          }
        } else {
          setRowData(masterTaget || []);
          //onChange(masterTaget || []);
        }

        setLoaded(true);
      }
    };

    setDetailData();
  }, [aprReqId, masterTaget]);

  const handleSearch = async (condition) => {
    const response = await findMultiUtMatrixLibraryList(condition);
    setSubTitle(condition?.stndEqpId || '');

    // 등록 요청이 아닐경우 version + 1
    if ((response || []).length && mode !== ManagementMode.CREATE) {
      const updatedRow = response.map(
        (item) =>
          ({
            ...item,
            utmLibVerNo:
              (typeof item.utmLibVerNo === 'number'
                ? item.utmLibVerNo
                : Number(item.utmLibVerNo ?? 0)) + 1,
          } as UtMatrixLibrary)
      );

      setRowData(updatedRow);
      onChange(updatedRow);
    } else {
      setRowData(response);
      onChange(response);
    }
  };

  const handleAddMultiRow = () => {
    setOpenStandardEquipmentMultiModal(true);
  };

  const handleAddRow = async () => {
    if (!selectedRow || selectedRow.length <= 0) {
      openMessageBar({
        type: 'error',
        content: t('ut.msg.대상설비를 선택 후 진행해주세요.', '대상설비를 선택 후 진행해주세요.'),
      });
      return;
    }
    if (selectedRow.length > 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.msg.대상설비는 한건만 선택해주세요.', '대상설비는 한건만 선택해주세요.'),
      });
      return;
    }

    const row = selectedRow[0] ?? selectedRow;

    const response = await findUtMatrixAllLibraryList({
      aprReqId: aprReqId,
      stndEqpId: row?.stndEqpId,
      //stndEqpId: selectedRow[0]?.stndEqpId,
    });

    const mergedRow = [...rowData, ...response].reduce<UtMatrixLibrary[]>((acc, item) => {
      const exist = acc.find((i) => i.utmLibId === item.utmLibId);
      if (exist) {
        Object.assign(exist, item); // 병합
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, []);

    const numbers = mergedRow
      .map((row) => {
        const match = row?.utmLibId?.match(/\d+$/); // 마지막 숫자 추출
        return match ? parseInt(match[0], 10) : null; // 숫자로 변환
      })
      .filter((num) => num !== null) as number[];

    const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
    const newNumber = (maxNumber + 1).toString().padStart(3, '0'); // 3자리 포맷

    const newRow = {
      prdnProcCdNm: row?.prdnProcCdNm,
      eqclIdNm: row?.eqclIdNm,
      stndEqpId: row?.stndEqpId,
      dtalProcCdNm: row?.dtalProcCdNm,
      eqpMainNm: row?.eqpMainNm,
      eqpMchNm: row?.eqpMchNm,
      crudKey: CrudCode.CREATE,
      utmLibId: row?.stndEqpId + '-' + newNumber,
      utmLibVerNo: 1,
    } as UtMatrixLibrary;
    const rows = [newRow, ...rowData];
    setRowData(rows);
    onChange(rows);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }
    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
    onChange(filteredData);
    onClickDetail(filteredData[0] || []);
    //setSelectedRow(filteredData[0] || []);

    gridRef.current?.collectionView.refresh();
  };

  const layoutDefinition = useMemo(() => {
    const mapUtVltgList = new DataMap(code?.utVltgList || [], 'cmnCd', 'cmnCdNm');
    mapUtVltgList.getDisplayValues = (item) => {
      return (code?.utVltgList || [])
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.cmnCd === cur.cmnCd) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .filter((o) => !!o.cmnCdNm)
        .map((o) => o.cmnCdNm || '');
    };

    const mapPhaseList = new DataMap(code?.elpwPhasCd || [], 'cmnCd', 'cmnCdNm');
    mapPhaseList.getDisplayValues = (item) => {
      // 선택한 Voltage 값에 따라 Phase 콤보박스 필터링
      const targets = (code?.utVltgPhasMapList || [])
        .filter((o) => o.optValCtn2 === item.vltgNvl)
        .map((o) => o.optValCtn3 || '');
      return (code?.elpwPhasCd || [])
        .filter((o) => !!o.cmnCdNm && (targets || []).includes(o.cmnCd))
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.cmnCd === cur.cmnCd) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .map((o) => o.cmnCdNm || '');
    };

    const mapWireList = new DataMap(code?.ctwTpCd || [], 'cmnCd', 'cmnCdNm');
    mapWireList.getDisplayValues = (item) => {
      const targets = (code?.utVltgPhasMapList || [])
        .filter((o) => o.optValCtn2 === item.vltgNvl && o.optValCtn3 === item.elpwPhasCd)
        .map((o) => o.optValCtn4 || '');
      return (code?.ctwTpCd || [])
        .filter((o) => !!o.cmnCdNm && (targets || []).includes(o.cmnCd))
        .reduce((acc, cur) => {
          if (acc.findIndex((o) => o.cmnCd === cur.cmnCd) < 0) {
            acc.push(cur);
          }
          return acc;
        }, [])
        .map((o) => o.cmnCdNm || '');
    };

    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        align: 'center',
        minWidth: 45,
        width: 45,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        align: 'center',
        minWidth: 70,
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군명', '설비군명')),
        align: 'center',
        minWidth: 70,
        width: 140,
        isReadOnly: true,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        align: 'center',
        minWidth: 70,
        width: 122,
        isReadOnly: true,
        cssClass: isCreate ? 'WijmoFind' : '',
        cellTemplate: (params) => `
          <span>${params.value || ''}</span>
          ${isCreate ? '<Button />' : ''} 
        `,
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정명', '세부공정명')),
        align: 'left',
        minWidth: 70,
        width: 150,
        isReadOnly: true,
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main 명', 'Main 명')),
        align: 'left',
        minWidth: 70,
        width: 110,
        isReadOnly: true,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine 명', 'Machine 명')),
        align: 'left',
        minWidth: 70,
        width: 140,
        isReadOnly: true,
      },
      {
        binding: 'utmLibId',
        header: String(t('ut.label.Library ID', 'Library ID')),
        align: 'center',
        minWidth: 70,
        width: 140,
        isReadOnly: true,
      },
      {
        header: String(t('ut.label.Version', 'Version')),
        columns: [
          {
            binding: 'utmLibVerNo',
            header: ' ',
            align: 'center',
            cssClass: 'noMerge',
            minWidth: 60,
            width: 60,
            isReadOnly: true,
          },
        ],
      },
      {
        binding: 'prjNm',
        header: String(t('ut.label.프로젝트', '프로젝트')),
        align: 'center',
        minWidth: 70,
        width: 100,
        isReadOnly: !isEditable,
        isRequired: false,
      },
      {
        binding: 'vdcpNm',
        header: String(t('ut.label.Vendor', 'Vendor')),
        align: 'center',
        minWidth: 70,
        width: 100,
        isReadOnly: !isEditable,
        isRequired: false,
      },
      {
        binding: 'genNm',
        header: String(t('ut.label.Generation', 'Generation')),
        align: 'center',
        minWidth: 70,
        width: 115,
        isReadOnly: !isEditable,
        isRequired: false,
      },
      {
        header: String(t('ut.label.Electricity', 'Electricity')),
        collapseTo: 'vltgNvl',
        columns: [
          {
            binding: 'vltgNvl',
            header: String(t('ut.label.Voltage', 'Voltage')),
            align: 'center',
            minWidth: 70,
            width: 90,
            cssClass: 'WijmoSelect',
            dataMap: mapUtVltgList,
            isReadOnly: !isEditable,
            isRequired: false,
          },
          {
            binding: 'elpwPhasCd',
            header: String(t('ut.label.Phase', 'Phase')),
            align: 'center',
            minWidth: 60,
            width: 75,
            cssClass: 'WijmoSelect',
            dataMap: mapPhaseList,
            isReadOnly: !isEditable,
            isRequired: false,
          },
          {
            binding: 'ctwTpCd',
            header: String(t('ut.label.Wire', 'Wire')),
            align: 'center',
            minWidth: 60,
            width: 90,
            cssClass: 'WijmoSelect',
            dataMap: mapWireList,
            isReadOnly: !isEditable,
            isRequired: false,
          },
          {
            binding: 'od2EcNvl',
            header: String(t('ut.label.Current(입력)', 'Current(입력)')),
            minWidth: 60,
            width: 110,
            align: 'right',
            format: 'n1',
            dataType: DataType.Number,
            isReadOnly: !isEditable,
            isRequired: false,
          },
          {
            binding: 'brkeCapa',
            header: String(t('ut.label.Breaker', 'Breaker')),
            minWidth: 60,
            width: 110,
            align: 'right',
            format: 'n1',
            dataType: DataType.Number,
            isReadOnly: !isEditable,
            isRequired: false,
          },
          {
            binding: 'elpwCapa',
            header: String(t('ut.label.Capacity', 'Capacity')),
            minWidth: 80,
            width: 95,
            isReadOnly: !isEditable,
            isRequired: false,
            align: 'right',
            dataType: DataType.Number,
            format: 'n1',
          },
        ],
      },
      {
        binding: 'cdaCspCapa',
        header: String(t('ut.label.CDA', 'CDA')),
        minWidth: 80,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'nitrPeakCspCapa',
        header: String(t('ut.label.N2', 'N2')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'iwCspCapa',
        header: String(t('ut.label.IW', 'IW')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'frwtCspCapa',
        header: String(t('ut.label.FW', 'FW')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'coolCspCapa',
        header: String(t('ut.label.PCW', 'PCW')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'wwCspCapa',
        header: String(t('ut.label.WW', 'WW')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'stemCspCapa',
        header: String(t('ut.label.ST', 'ST')),
        minWidth: 70,
        width: 85,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'lngCspCapa',
        header: String(t('ut.label.NG', 'NG')),
        minWidth: 70,
        width: 110,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'insdExhaCspCapa',
        header: String(t('ut.label.RA', 'RA')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'otsdExhaCspCapa',
        header: String(t('ut.label.EA', 'EA')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'suarCspCapa',
        header: String(t('ut.label.SA', 'SA')),
        minWidth: 70,
        width: 95,
        isReadOnly: !isEditable,
        isRequired: false,
        dataType: DataType.Number,
        align: 'right',
        format: 'n1',
      },
      {
        binding: 'rmk',
        header: String(t('ut.label.Remark', 'Remark')),
        align: 'left',
        width: 110,
        isReadOnly: !isEditable,
        isRequired: false,
      },
    ];
  }, [code, masterTaget]);

  const onCellEditEnding = useEvent((grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;

    // 전압값수치
    if ('vltgNvl' === binding) {
      const utVltg = (code?.utVltgList || []).filter((o) => o.cmnCd === newVal);
      if (utVltg.length) {
        // 전기위상코드 선택 (ref. 전압값수치)
        const elpwPhasCd = (code?.elpwPhasCd || []).filter(
          (o) => o.cmnCd === utVltg[0]?.optValCtn3
        );
        data.elpwPhasCd = elpwPhasCd.length ? elpwPhasCd[0].cmnCd : '';
        // 단선유형코드 선택 (ref. 전기위상코드)
        if (elpwPhasCd.length) {
          const ctwTpCd = (code?.ctwTpCd || []).filter(
            (o) => o.cmnCd === elpwPhasCd[0]?.optValCtn1
          );
          data.ctwTpCd = ctwTpCd.length ? ctwTpCd[0].cmnCd : '';
        }
      } else {
        data.elpwPhasCd = '';
        data.ctwTpCd = '';
      }
    } else if ('elpwPhasCd' === binding) {
      const elpwPhasCd = (code?.elpwPhasCd || []).filter((o) => o.cmnCd === newVal);
      // 단선유형코드 선택 (ref. 전기위상코드)
      if (elpwPhasCd.length) {
        const ctwTpCd = (code?.ctwTpCd || []).filter((o) => o.cmnCd === elpwPhasCd[0]?.optValCtn1);
        data.ctwTpCd = ctwTpCd.length ? ctwTpCd[0].cmnCd : '';
      } else {
        data.ctwTpCd = '';
      }
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.ColumnHeader === panel.cellType) {
      const binding = panel.columns[col].binding;
      // 필수항목
      if (['genNm', 'vdcpNm'].includes(binding)) {
        addClass(cell, 'dot');
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler(function (s, e) {
      if (s.cells === e.panel) {
        const column = s.columns[e.col];
        const format = column.format;
        const dataType = column.dataType;
        if (dataType === DataType.Number && format) {
          // [25.10.05] 자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
          const binding = column.binding;
          s.rows[e.row].dataItem[binding] = e.cell.innerText ? convertNumber(e.cell.innerText) : '';
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        // 상세보기
        if ('stndEqpId' === binding) {
          // 신규인 경우에만 설비코드 변경 가능
          if (ManagementMode.CREATE === mode) {
            setOpenStandardEquipmentModal(true);
            setTargetCondition({
              row: ht.row,
              col: ht.col,
            });
          }
        } else {
          setSelectedRow(item);
          onClickDetail(item);
        }
      }
    });
  };

  // const onInitialized = (grid) => {
  //   gridRef.current = grid;
  //   grid.itemFormatter = onItemFormatter;
  //
  //   grid.formatItem.addHandler((s, e) => {
  //     const col = e.panel.columns[e.col];
  //     const row = e.panel.rows[e.row];
  //
  //     if (e.panel.cellType !== CellType.Cell) {
  //       return;
  //     }
  //
  //     const ht = grid.hitTest(e);
  //     if (ht.row < 0 || ht.col < 0) return;
  //
  //     // 상태 열 스타일 적용
  //     // if ('crudKey' === col.binding) {
  //     //   const item = s.rows[e.row].dataItem;
  //     //   if (mode === ManagementMode.CREATE) {
  //     //     item.crudKey = CrudCode.CREATE;
  //     //   }
  //     //   if (mode === ManagementMode.DELETE) {
  //     //     item.crudKey = CrudCode.DELETE;
  //     //   }
  //     //   e.cell.innerHTML = GridStatusCellTemplate({ item: item });
  //     //   e.cell.style.textAlign = 'center';
  //     // }
  //
  //     const binding = grid.columns[ht.col].binding;
  //     const item = grid.rows[ht.row].dataItem;
  //
  //     if (ht.panel === grid.cells) {
  //       // 상세보기
  //       if ('stndEqpId' === binding) {
  //         // 신규인 경우에만 설비코드 변경 가능
  //         if (ManagementMode.CREATE === mode) {
  //           setOpenStandardEquipmentModal(true);
  //           setTargetCondition({
  //             row: ht.row,
  //             col: ht.col,
  //           });
  //         }
  //       } else {
  //         setSelectedRow(item);
  //         onClickDetail(item);
  //       }
  //     }
  //   });
  // grid.formatItem.addHandler((s, e) => {
  //   const col = e.panel.columns[e.col];
  //   const row = e.panel.rows[e.row];
  //
  //   if (e.panel.cellType !== CellType.Cell) {
  //     return;
  //   }
  //
  //   // 상태 열 스타일 적용
  //   if ('crudKey' === col.binding) {
  //     const item = s.rows[e.row].dataItem;
  //     if (mode === ManagementMode.CREATE) {
  //       item.crudKey = CrudCode.CREATE;
  //     }
  //     if (mode === ManagementMode.DELETE) {
  //       item.crudKey = CrudCode.DELETE;
  //     }
  //     e.cell.innerHTML = GridStatusCellTemplate({ item: item });
  //     e.cell.style.textAlign = 'center';
  //   }
  // });

  // grid.hostElement.addEventListener('click', (e) => {
  //   if (grid.rows.length == 0) return;
  //
  //   const ht = grid.hitTest(e);
  //   if (ht.row < 0 || ht.col < 0) return;
  //
  //   const binding = grid.columns[ht.col].binding;
  //   const item = grid.rows[ht.row].dataItem;
  //
  //   if (ht.panel === grid.cells) {
  //     // 상세보기
  //     if ('stndEqpId' === binding) {
  //       // 신규인 경우에만 설비코드 변경 가능
  //       if (ManagementMode.CREATE === mode) {
  //         setOpenStandardEquipmentModal(true);
  //         setTargetCondition({
  //           row: ht.row,
  //           col: ht.col,
  //         });
  //       }
  //     } else {
  //       setSelectedRow(item);
  //       onClickDetail(item);
  //     }
  //   }
  // });
  // };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix Library', 'UT Matrix Library')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {isEditable && !isReadOnly && (
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="plus"
              onClick={handleAddMultiRow}
              disableRipple
            >
              {t('com.button.표준설비코드', '표준설비코드')}
            </Button>
            <Button css={IconButton.button} className="plus" onClick={handleAddRow} disableRipple>
              {t('com.button.행추가', '행추가')}
            </Button>
            <Button css={IconButton.button} className="minus" onClick={handleDelRow} disableRipple>
              {t('com.button.행삭제', '행삭제')}
            </Button>
            <Button css={IconButton.button} className="refresh" onClick={onRefresh} disableRipple>
              {t('com.button.새로고침', '새로고침')}
            </Button>
          </ControlBtnGroup>
        )}
      </SubTitleLayout>

      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData || []}
        isSelector={[ManagementMode.CREATE, ManagementMode.MODIFY].includes(mode)}
        showCheckAll={false}
        isFilter={false}
        autoCheck={true}
        allowDragging={false}
        isReadOnly={isReadOnly}
        height={gridHeight}
        initialized={onInitialized}
        excludePin={[
          'prjNm',
          'vdcpNm',
          'genNm',
          'vltgNvl',
          'elpwPhasCd',
          'ctwTpCd',
          'od2EcNvl',
          'brkeCapa',
          'elpwCapa',
          'cdaCspCapa',
          'nitrPeakCspCapa',
          'iwCspCapa',
          'frwtCspCapa',
          'coolCspCapa',
          'wwCspCapa',
          'stemCspCapa',
          'lngCspCapa',
          'insdExhaCspCapa',
          'otsdExhaCspCapa',
          'suarCspCapa',
          'rmk',
        ]}
        cellEditEnding={onCellEditEnding}
        onChangeCheckedItem={(items) => setSelectedRow(items)}
        loadedRows={(grid, e) => {
          if (isEditable) {
            // 전체 행 편집모드 활성화 후 특정 컬럼 readonly 처리는 itemFormatter에서 처리
            grid.rows.forEach((row) => {
              row.isReadOnly = false;
            });
          }
        }}
        cellEditEnded={useEvent((grid, e) => {
          const binding = grid.columns[e.col].binding;
          const value = grid.getCellData(e.row, e.col);
          onChange(rowData);
        })}
      />

      {isOpenStandardEquipmentMultiModal && (
        <StandardEquipmentPopup
          open={isOpenStandardEquipmentMultiModal}
          close={() => setOpenStandardEquipmentMultiModal(false)}
          onCallback={async (target) => {
            if (target) {
              const masterList = target;

              const targetIdSet = new Set(masterList.map((item) => item.stndEqpId));
              const validIds = rowData.some((item) => targetIdSet.has(item.stndEqpId));

              if (validIds) {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'ut.msg.설비코드는 중복 선택할 수 없습니다.',
                    '설비코드는 중복 선택할 수 없습니다.'
                  ),
                });
                return false;
              }

              await checkAvailableRequest(mode, masterList).then(async (res) => {
                if (res.successOrNot === 'Y' && res.data) {
                  // const newRows = masterList.map((item) => ({
                  //   ...item,
                  //   utmLibVerNo: 1,
                  //   crudKey: CrudCode.CREATE,
                  // }));

                  const ids = (masterList || []).map((item) => item.stndEqpId);
                  const response = await findMultiUtMatrixAllLibraryList({
                    aprReqId: aprReqId,
                    stndEqpIds: ids,
                    //stndEqpId: selectedRow[0]?.stndEqpId,
                  });

                  const mergedRow = [...rowData, ...response].reduce<UtMatrixLibrary[]>(
                    (acc, item) => {
                      const exist = acc.find((i) => i.utmLibId === item.utmLibId);
                      if (exist) {
                        Object.assign(exist, item); // 병합
                      } else {
                        acc.push({ ...item });
                      }
                      return acc;
                    },
                    []
                  );

                  const numbers = mergedRow
                    .map((row) => {
                      const match = row?.utmLibId?.match(/\d+$/); // 마지막 숫자 추출
                      return match ? parseInt(match[0], 10) : null; // 숫자로 변환
                    })
                    .filter((num) => num !== null) as number[];

                  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
                  const newNumber = (maxNumber + 1).toString().padStart(3, '0'); // 3자리 포맷

                  const newRows = masterList.map((item) => ({
                    ...item,
                    utmLibId: item?.stndEqpId + '-' + newNumber,
                    utmLibVerNo: 1,
                    crudKey: CrudCode.CREATE,
                  }));

                  const filteredEmptyRows = rowData.filter(
                    (row) => row.stndEqpId && row.stndEqpId !== ''
                  );

                  const rows = [...newRows, ...filteredEmptyRows];
                  setRowData(rows);
                  onChange(rows);
                } else {
                  openMessageBar({
                    type: 'error',
                    content:
                      res.data && typeof res.data === 'string'
                        ? res.data
                        : t(
                            'ut.msg.처리 중 오류가 발생하였습니다.',
                            '처리 중 오류가 발생하였습니다.'
                          ),
                  });
                  return false;
                }
              });
            }
          }}
          singleSelect={false}
        />
      )}
      {isOpenStandardEquipmentModal && (
        <StandardEquipmentPopup
          open={isOpenStandardEquipmentModal}
          close={() => setOpenStandardEquipmentModal(false)}
          onCallback={async (target) => {
            if (typeof targetCondition?.row === 'number') {
              const masterList = target ? [{ ...target }] : [];

              const targetIdSet = new Set(masterList.map((item) => item.stndEqpId));
              const validIds = rowData.some((item) => targetIdSet.has(item.stndEqpId));
              if (validIds) {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'ut.msg.설비코드는 중복 선택할 수 없습니다.',
                    '설비코드는 중복 선택할 수 없습니다.'
                  ),
                });
                return false;
              }

              await checkAvailableRequest(mode, masterList).then(async (res) => {
                if (res.successOrNot === 'Y' && res.data) {
                  const selectedRow = gridRef.current.rows[targetCondition?.row].dataItem;

                  const stndEqpId = (target as UtMatrixStandardEquipment).stndEqpId;
                  gridRef.current.setCellData(
                    targetCondition?.row,
                    targetCondition?.col,
                    stndEqpId
                  );

                  const ids = (masterList || []).map((item) => item.stndEqpId);
                  const response = await findMultiUtMatrixAllLibraryList({
                    aprReqId: aprReqId,
                    stndEqpIds: ids,
                  });

                  const mergedRow = [...rowData, ...response].reduce<UtMatrixLibrary[]>(
                    (acc, item) => {
                      const exist = acc.find((i) => i.utmLibId === item.utmLibId);
                      if (exist) {
                        Object.assign(exist, item); // 병합
                      } else {
                        acc.push({ ...item });
                      }
                      return acc;
                    },
                    []
                  );

                  const numbers = mergedRow
                    .map((row) => {
                      const match = row?.utmLibId?.match(/\d+$/); // 마지막 숫자 추출
                      return match ? parseInt(match[0], 10) : null; // 숫자로 변환
                    })
                    .filter((num) => num !== null) as number[];

                  const maxNumber = numbers.length > 0 ? Math.max(...numbers) : 0;
                  const newNumber = (maxNumber + 1).toString().padStart(3, '0'); // 3자리 포맷

                  selectedRow.prdnProcCd = target.prdnProcCd;
                  selectedRow.prdnProcCdNm = target.prdnProcCdNm;
                  selectedRow.eqclId = target.eqclId;
                  selectedRow.eqclIdNm = target.eqclIdNm;
                  selectedRow.dtalProcCd = target.dtalProcCd;
                  selectedRow.dtalProcCdNm = target.dtalProcCdNm;
                  selectedRow.eqpMainId = target.eqpMainId;
                  selectedRow.eqpMainNm = target.eqpMainNm;
                  selectedRow.eqpMchId = target.eqpMchId;
                  selectedRow.eqpMchNm = target.eqpMchNm;
                  selectedRow.libCnt = target.libraryQty;
                  selectedRow.utmLibId = stndEqpId + '-' + newNumber;
                  selectedRow.utmLibVerNo = 1;
                  gridRef.current?.refresh();
                } else {
                  openMessageBar({
                    type: 'error',
                    content:
                      res.data && typeof res.data === 'string'
                        ? res.data
                        : t('ut.msg.수정 요청할 수 없습니다.', '수정 요청할 수 없습니다.'),
                  });
                }
              });
            }
          }}
          singleSelect={true}
        />
      )}
    </>
  );
};

export default React.forwardRef(EditUtMatrixLibraryGrid);
